import { BehaviorSubject, EMPTY, merge, of, ReplaySubject, Subscription, timer } from 'rxjs';
import { Injectable, NgZone } from '@angular/core';
import {
    AuthMode,
    authModeKey,
    authTokensKey,
    LoginState,
    MSALErrorCode,
    VippsCustomErrorCode,
    VippsErrorCode
} from '@core/authentication/authentication.models';
import { MsalAuthenticationService } from '@core/authentication/msal-authentication.service';
import { VippsAuthenticationService } from '@core/authentication/vipps-authentication.service';
import { GtmService } from '@core/services/gtm/gtm.service';
import { catchError, distinctUntilChanged, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { ClubService } from '@features/club/service/club.service';
import { CustomerViewModel } from '@models/api-customers.models';
import { RelewiseTrackingService } from '@core/services/relewise/relewise-tracking.service';
import { StorageService } from '@core/services/storage.service';
import { NotificationService } from '@core/services/notification/notification.service';
import isEqual from 'lodash.isequal';
import { extractQueryParams } from '@core/authentication/authentication.helper';
import { unauthorizedContentRedirectKey } from '@core/authentication/redirect-util';
import { NavigationEnd, Router } from '@angular/router';
import { TranslationKey } from '@shared/translations/auto-generated/translation-key';

type LoginResponseQueryParams = {
    token?: string;
    expiresAt?: string;
    error?: string;
};

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    private readonly loginStateRs$ = new BehaviorSubject<LoginState>({ loggedIn: false });
    private readonly isReadyRs$ = new ReplaySubject<boolean>(1);
    private routerEvents$: Subscription | null = null;

    public isReady$ = this.isReadyRs$.asObservable();
    public loginStateSnapshot: LoginState = { loggedIn: false };
    public loginState$ = this.loginStateRs$.asObservable();

    constructor(
        private router: Router,
        private ngZone: NgZone,
        private gtmService: GtmService,
        private clubService: ClubService,
        private storageService: StorageService,
        private notificationService: NotificationService,
        private relewiseTrackingService: RelewiseTrackingService,
        private msalAuthenticationService: MsalAuthenticationService,
        private vippsAuthenticationService: VippsAuthenticationService
    ) {
        this.loginStateRs$.pipe(distinctUntilChanged((a, b) => isEqual(a, b))).subscribe(loginState => {
            if (loginState.loggedIn) {
                this.trackSignIn();
            }
        });

        merge(this.msalAuthenticationService.loginState$, this.vippsAuthenticationService.loginState$)
            .pipe(
                catchError((loginStateSnapshotError: LoginState) => {
                    this.redirectToAndNotify(loginStateSnapshotError);
                    return EMPTY;
                })
            )
            .subscribe({
                next: loginStateSnapshot => {
                    this.loginStateSnapshot = loginStateSnapshot;
                    this.loginStateRs$.next(this.loginStateSnapshot);
                }
            });

        // Initialize the authentication services
        this.initializeAuthServices();
    }

    public login(source?: AuthMode, email?: string, silentOnly?: boolean): void {
        if (source === 'Vipps') {
            this.vippsAuthenticationService.init(source);
            return this.vippsAuthenticationService.login();
        }

        if (source === 'AzureB2C') {
            this.msalAuthenticationService.init(source);
            this.msalAuthenticationService.login(email, silentOnly);
            return;
        }

        throw new Error('Invalid login source');
    }

    public logout(redirectUriExtension: string = ''): Promise<void> {
        const source = this.loginStateSnapshot.authMode ?? this.storageService.get(authModeKey);

        if (source === 'Vipps') {
            return this.vippsAuthenticationService.logout();
        }

        if (source === 'AzureB2C') {
            return this.msalAuthenticationService.logout(redirectUriExtension);
        }

        throw new Error('Invalid login source');
    }

    public updateLoginState(authMode: AuthMode, response?: string): void {
        if (authMode === 'Vipps') {
            const { token, expiresAt, error } = extractQueryParams<LoginResponseQueryParams>(response);
            if ((token && expiresAt) || error) {
                this.vippsAuthenticationService.updateToken(
                    token,
                    expiresAt,
                    error as VippsErrorCode & VippsCustomErrorCode
                );
                this.initializeAuthServices();
                return;
            }

            this.vippsAuthenticationService.logout();
            return;
        }

        if (authMode === 'AzureB2C') {
            const { error } = extractQueryParams<LoginResponseQueryParams>(response?.split('&')[0]);
            this.msalAuthenticationService.updateToken(error as MSALErrorCode);
            this.initializeAuthServices();
        }
    }

    public getActiveToken(): string | undefined {
        const token = this.storageService.get(authTokensKey);

        if (this.loginStateSnapshot.authMode === 'Vipps') {
            return token;
        } else {
            return this.msalAuthenticationService.getCurrentToken();
        }
    }

    public clearActiveToken(): void {
        if (this.loginStateSnapshot.authMode === 'Vipps') {
            this.vippsAuthenticationService.clearTokens();
        }
    }

    public resetLoginState(state: LoginState): void {
        this.loginStateRs$.next(state);
        if (state.authMode === 'Vipps' && state.error) {
            this.triggerErrorNotification(state);
        }
    }

    public getLoginStateSnapshot(): LoginState {
        return {
            authMode: this.storageService.get(authModeKey),
            loggedIn: this.storageService.get(authTokensKey) !== null
        };
    }

    private async initializeAuthServices(): Promise<void> {
        const authMode: AuthMode | undefined = this.storageService.get(authModeKey);

        if (authMode === 'Vipps') {
            this.vippsAuthenticationService.init();
            this.checkRedirect();
            this.isReadyRs$.next(true);
            return;
        }

        if (authMode === 'AzureB2C') {
            return this.msalAuthenticationService.init().then(() => this.isReadyRs$.next(true));
        }
    }

    /**
     * Redirect to the front page and show an error notification
     * Yeah... it's ugly and doesn't make sense, but it's the way i found to manage the correct redirect to the front page in
     * case of error for all possible scenarios.
     * @param loginStateSnapshotError
     * @private
     */
    private redirectToAndNotify(loginStateSnapshotError: LoginState): void {
        this.ngZone.runOutsideAngular(() => {
            setTimeout(() => {
                this.ngZone.run(() => {
                    this.redirectToFront('/', true).then(() => {
                        this.triggerErrorNotification(loginStateSnapshotError);
                    });
                });
            }, 500);
        });
    }

    // Push tracking events after user sign in
    private trackSignIn(): void {
        // GTM
        this.gtmService.isGtmReady$
            .pipe(
                withLatestFrom(this.loginState$),
                switchMap(([_, loginState]) =>
                    (loginState.loggedIn ? this.clubService.getUserInfo() : of<CustomerViewModel>({})).pipe(
                        map(userInfo => ({ userInfo, loginState }))
                    )
                ),
                map(({ userInfo, loginState }) => ({ ...userInfo, ...loginState })),
                take(1)
            )
            .subscribe(({ customerNo, verificationStage, username, loggedIn }) => {
                if (loggedIn) {
                    // Relewise
                    this.relewiseTrackingService.trackUser(username);

                    // Tracking the club_login sign in status
                    // when the user is logged in but did not confirm the account yet (without the customerNo and verification stage is not verified)
                    this.gtmService.trackingEvents?.trackUserSignIn(customerNo ?? '', verificationStage === 'Verified');
                }
            });
    }

    private triggerErrorNotification(loginState?: LoginState): void {
        if (!loginState?.error) {
            return;
        }

        const { error, authMode } = loginState;
        const key = authMode === 'Vipps' ? `vipps.error` : `msal.error`;

        // Adding some time to the application reload after the redirect
        timer(500)
            .pipe(take(1))
            .subscribe(() => {
                this.notificationService.createNotification({
                    fadeTime: 0,
                    translationHeadline: `${key}.notificationErrorHeadline` as TranslationKey,
                    translationText: `${key}.${error}` as TranslationKey,
                    type: 'error',
                    showHeadline: true
                });
            });
    }

    /**
     * Check if the user is redirected from Vipps and update the
     * token if the user is logged in or show an error notification if the user is not logged in or hit the wrong challenge
     * @private
     */
    private checkRedirect(): void {
        let path: string;
        const contentUserAttemptedToAccess = this.storageService.get(unauthorizedContentRedirectKey);
        if (contentUserAttemptedToAccess) {
            path = contentUserAttemptedToAccess;
        } else {
            path = this.router.url;
        }

        if (this.routerEvents$) {
            this.routerEvents$.unsubscribe();
        }

        this.routerEvents$ = this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                map(event => {
                    const queryParams = event.url.includes('?') ? event.url.split('?') : [];

                    if (!queryParams.length) {
                        return {
                            url: event.url,
                            loggedIn: false
                        };
                    }

                    const { token, expiresAt, error } = extractQueryParams<LoginResponseQueryParams>(event.url);
                    if (error) {
                        throw new Error(error);
                    }

                    return {
                        url: event.url,
                        loggedIn: !!token,
                        token,
                        expiresAt
                    };
                })
            )
            .subscribe({
                next: ({ url, loggedIn, token, expiresAt }) => {
                    if (url === `/${path}`) {
                        this.storageService.remove(unauthorizedContentRedirectKey);
                    }

                    if (token && loggedIn) {
                        this.vippsAuthenticationService.updateToken(token, expiresAt);
                    }
                },
                error: ({ message }) => {
                    this.ngZone.runOutsideAngular(() => {
                        setTimeout(() => {
                            // to be sure we only show the notification after the redirect
                            this.redirectToFront(path, true).then(() => {
                                this.triggerErrorNotification({ loggedIn: false, authMode: 'Vipps', error: message });
                            });
                        }, 1000);
                    });
                    this.vippsAuthenticationService.updateToken();
                }
            });
    }

    private redirectToFront(path: string, replace: boolean = true): Promise<boolean> {
        return this.router.navigateByUrl(path, { replaceUrl: replace });
    }
}
